/**
 * Component to render SCA Annual report page
 */
const AnnualReportPage = (): React.ReactElement => {
  return (
    <div
      className="mb-10 font-public-sans"
      data-testid="annual-report-page-component"
    >
      <p>
        <strong>Objectives of the Award</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          To encourage excellent corporate reporting and a wider scope of
          disclosures beyond the minimum regulatory requirements that are in
          tandem with the needs of investors and other stakeholders such as
          employees, creditors and the general public;
        </li>
        <li>
          The Best Annual Report Award was introduced to increase awareness,
          through the encouragement of social responsibility reporting that
          businesses and organisations are responsible to the community both as
          employers and corporate citizens;
        </li>
        <li>
          The Best Annual Report Award also gives recognition to maiden annual
          reports of First-Year Listed companies that show an excellent standard
          of disclosure. The Judging Panel hopes that this will encourage
          First-Year Listed companies to raise the standard of their annual
          report disclosures;
        </li>
      </ul>

      <p>
        <strong>Categories of the Award</strong>
      </p>

      <p>
        The Award will be presented for the following categories of companies:
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Listed companies with market capitalisation of S$1 billion and above;
        </li>
        <li>
          Listed companies with market capitalisation of S$300 million to less
          than S$1 billion;
        </li>
        <li>
          Listed companies with market capitalisation of less than S$300
          million;
        </li>
        <li>REITS and Business Trusts;</li>
        <li>First-Year Listed companies;</li>
      </ul>

      <p>
        <strong>Screening Process</strong>
      </p>

      <p>
        Annual reports of all SGX-listed companies will go through a two-stage
        screening process. All annual reports are screened preliminarily by
        reference to the presence of key information stipulated in the Best
        Annual Report Award preliminary screening criteria.
      </p>

      <p>
        The Best Annual Report Award Judging Panel subsequently assesses each
        shortlisted annual report against the detailed marking criteria, taking
        into account the quality and presentation of information.
      </p>

      <p>
        <strong>Preliminary Screening Criteria</strong>
      </p>

      <p>
        The annual reports are first screened for the presence of the following
        information:
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>Discussion on corporate liquidity or capital management;</li>
        <li>
          Benchmarking commentaries against industry performance/statistics;
        </li>
        <li>Analysis and commentary on performance;</li>
        <li>Separate CEO/management&apos;s statement;</li>
        <li>Earnings prospects by division/territory;</li>
        <li>Discussion of key business risks;</li>
        <li>Discussion of risk management strategies;</li>
        <li>
          Presence of quantitative analysis of identified risks, including
          value-at-risk (VAR) analysis;
        </li>
        <li>
          Discussion on information technology risks, cyber security and
          personal data protection;
        </li>
        <li>
          Remuneration of directors disclosed in nearest dollar amount and not
          in bands;
        </li>
        <li>
          Remuneration of key management personnel disclosed in dollar amount
          and not in bands;
        </li>
        <li>Board succession planning/long tenure of independent directors;</li>
        <li>Policy/position on board diversity;</li>
        <li>
          Sustainability report disclosures included in annual report
          <ul className="list-outside list-disc pl-8">
            <li>Environment</li>
            <li>Community and social programmes</li>
          </ul>
        </li>
        <li>Quantification of sustainability performance;</li>
        <li>External assurance (including limited or reasonable assurance);</li>
        <li>Investor/shareholder relations activities;</li>
        <li>
          Employee diversity disclosure (gender, age, education, nationality,
          people with special needs, etc.);
        </li>
        <li>
          (Specific to REITs and Business Trusts) Disclosure of
          business-specific measures of historical performance for portfolios
          (i.e. such as trademix by area or trademix by gross rent of lease
          expiry profile etc.);
        </li>
        <li>
          (Specific to REITs and Business Trusts) Presence of financial
          highlights commentary (discussions on financial statistics presented
          like ROE, dividend cover, etc.);
        </li>
        <li>
          (Specific to REITs and Business Trusts) Discussion on corporate
          liquidity or capital management (description of loan and borrowing
          profile);
        </li>
        <li>
          (Specific to REITs and Business Trusts) Disclosure of REIT/business
          trust manager relationship;
        </li>
        <li>
          (Specific to REITs and Business Trusts) Earnings prospects by
          portfolios;
        </li>
        <li>
          (Specific to REITs and Business Trusts) Disclosure of group and
          strategic directions (clear articulation of vision, mission statement,
          goals and objectives);
        </li>
      </ul>

      <p>
        <strong>Detailed Marking Criteria</strong>
      </p>

      <p>
        Annual reports shortlisted from the preliminary screening process are
        examined in detail for the quality of information disclosed according to
        the assessment criteria in the following areas:
      </p>

      <p>
        <strong>Performance Review (PR)</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Commentaries on historical analysis and ratios (5-year, 10-year trends
          etc.);
        </li>
        <li>
          Analysis and commentaries on current year financials - quarterly,
          half-yearly, segment analysis;
        </li>
        <li>
          Commentaries on capital management (gearing, debt equity ratios,
          managing borrowing, refinancing etc.);
        </li>
        <li>
          Benchmarking against competitors and industry performance (e.g. total
          shareholder returns, share prices);
        </li>
        <li>
          Discussion on how the Group has performed in relation to its vision
          and mission;
        </li>
        <li>Group structure and business segment;</li>
        <li>
          (Specific to REITs and Business Trusts) Description of portfolio/asset
          characteristics (e.g. valuation as at latest date, committed
          occupancy, tenancy mix, top tenants, lease expiry profile);
        </li>
        <li>
          (Specific to REITs and Business Trusts) Detailed description on yield
          accretive acquisitions and asset disposals (including disclosure of
          acquisition/disposal fee payment), asset enhancement initiatives,
          lease renewals;
        </li>
        <li>
          (Specific to REITs and Business Trusts) Current year ratios (DPU,
          distribution yields, gearing, interest cover and productivity) and
          commentaries;
        </li>
      </ul>

      <p>
        <strong>Business Plan and Prospects (BPP)</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Corporate vision/mission, strategic goals, strategy and business
          environment;
        </li>
        <li>
          Business plan and initiatives and planned capital investment (forward
          looking);
        </li>
        <li>
          Outlook and future prospects, including prospects by territory and
          business division;
        </li>
        <li>Corporate/industry developments and strategic issues;</li>
      </ul>

      <p>
        <strong>Risk Assessment and Management (RAM)</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>Discussion on risk appetite, risk-aware culture and policies;</li>
        <li>
          Identification and assessment of key risks including climate-related
          risks;
        </li>
        <li>
          Quantitative assessment of key risks (e.g. VaR, sensitivity analysis,
          stress testing, cyber risk);
        </li>
        <li>Risk management responses/strategies;</li>
        <li>Crisis management and business continuity planning;</li>
        <li>
          Discussion on how the entity mitigated information technology and
          cybersecurity risks, including whether a cybersecurity audit had been
          performed, and personal data protection (if applicable);
        </li>
      </ul>

      <p>
        <strong>Corporate Governance Issues (CG)</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Quality of disclosures on the Group&apos;s policy and practices on
          diversity within the Board;
        </li>
        <li>Board assessment of director&apos;s independence;</li>
        <li>Board succession planning for independent directors;</li>
        <li>Succession planning for key management;</li>
        <li>Management background information;</li>
        <li>
          Director remuneration (including breakdown, bands, dollar amounts),
          including quality of explanation for non-compliance, if any;
        </li>
        <li>
          Key executive remuneration (including breakdown, bands, dollar
          amounts), including quality of explanation for non-compliance, if any;
        </li>
        <li>Director orientation and training policy and processes;</li>
        <li>
          Key executive and director performance, evaluation and remuneration
          policy and processes, including link to environmental, social and
          governance performance;
        </li>
        <li>
          Fraud and whistle-blowing policy and processes (including procedures
          and any reported instances);
        </li>
        <li>
          Board opinion of internal controls (including Board&apos;s basis for
          the opinion);
        </li>
        <li>
          Commentary on the Group&apos;s key audit risk areas (e.g. audit
          committee/corporate governance report or a separate report);
        </li>
        <li>Discussion on the Group&apos;s dividend policy;</li>
        <li>
          Discussion on adequacy, effectiveness, independence, scope and results
          of internal audit function (including review of sustainability
          reporting processes);
        </li>
        <li>
          (Specific to REITs and Business Trusts) Relationship between sponsor,
          manager/trustee-manager and asset/property manager (roles/checks and
          balances residing with each entity, resolution of conflicts of
          interest particularly if there are common directors);
        </li>
      </ul>

      <p>
        <strong>Environmental and Social Accountability (ESA)</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Social and environmental sustainability practices, policy and plan;
        </li>
        <li>
          Quantitative and qualitative disclosures on sustainability performance
          relating to climate (e.g. climate reporting based on TCFD
          Recommendations, greenhouse gase emissions, energy efficiency);
        </li>
        <li>
          Quantitative and qualitative disclosures on other environmental
          sustainability performance (e.g. water use, waste and pollution, land
          use and biodiversity);
        </li>
        <li>
          Quantitative and qualitative disclosures on employees (e.g. evidence
          on fair practices, training, non-discriminatory policy, health and
          safety policies);
        </li>
        <li>
          Quantitative and qualitative disclosures on wider community (e.g.
          suppliers, customers, society);
        </li>
        <li>
          Discussion of the company&apos;s sustainability targets and
          performance and its progress in meeting those targets and performance;
        </li>
        <li>
          Discussion of sustainability reporting with reference to frameworks
          like GRI, SASB and TCFD or reference to a separate sustainability
          report with the information;
        </li>
        <li>
          Discussion of how the entity supported the United Nations&apos;
          Sustainable Development Goals;
        </li>
        <li>
          Quality of external assurance report for sustainability report
          (additional points if reasonable assurance has been obtained (as
          opposed to limited assurance));
        </li>
      </ul>

      <p>
        <strong>Informativeness, Clarity and Presentation (ICP)</strong>
      </p>

      <ul className="list-outside list-disc pb-6 pl-8">
        <li>
          Comprehensive and informative (i.e. no omission of crucial and useful
          information, investor/shareholder relations activities, application of
          Intangibles Disclosure Framework);
        </li>
        <li>
          Clear and well-organised (i.e. good use of diagrams/charts, easy
          retrieval of specific information);
        </li>
        <li>
          Innovative design and presentation (i.e. attractive, refreshing and
          novel presentation);
        </li>
      </ul>

      <div className="mt-6 text-xs">
        <p>For more information, please contact:</p>

        <ul className="mb-6 list-none">
          <li>Mr Terence Lam</li>
          <li>Email: terence.lam@isca.org.sg</li>
          <li>DID: +65 6597 5688</li>
        </ul>

        <ul className="mb-6 list-none">
          <li>Institute of Singapore Chartered Accountants</li>
          <li>60 Cecil Street, ISCA House</li>
          <li>Singapore 049709</li>
        </ul>

        <p>OR</p>

        <ul className="mb-6 list-none">
          <li>Mr Donaphan Boey</li>
          <li>Email: donaphan.boey@isca.org.sg</li>
          <li>DID: +65 6597 5627</li>
        </ul>

        <ul className="list-none">
          <li>Institute of Singapore Chartered Accountants</li>
          <li>60 Cecil Street, ISCA House</li>
          <li>Singapore 049709</li>
        </ul>
      </div>
    </div>
  );
};

export default AnnualReportPage;
